/* eslint-disable @typescript-eslint/no-explicit-any */
import { CustomerInfo, LogLevel, Purchases } from "@revenuecat/purchases-js";
import { configureAutoTrack } from "aws-amplify/analytics";
import axios from "axios";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { GraphQLQuery, GraphQLSubscription, post } from "@aws-amplify/api";
import {
  FetchUserAttributesOutput,
  GetCurrentUserOutput,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import Mixpanel from "mixpanel-browser";
import BoxLoader from "@/components/BoxLoader";
import { getUser } from "@/graphql/queries";
import { onUpdateUser } from "@/graphql/subscriptions";
import {
  GetUserQuery,
  GetUserQueryVariables,
  OnUpdateUserSubscription,
  OnUpdateUserSubscriptionVariables,
} from "@/API";
import { useTemplateMigrationTrigger } from "@/hooks/useTemplateMigrationTrigger";
import { userMigrationService } from "@/services/UserMigrationService";
import { supabase } from "@/lib/supabaseClient";

const client = generateClient();

interface UserProps {
  revcatUser: UserState;
  amplifyUser: any;
  recordings: Recording[];
  setRecordings?: (recordings: Recording[]) => void;
  mixpanel: any;
  initRevcat: () => Promise<UserState | null | undefined>;
  revokeRevcatPromotion: () => Promise<void>;
  resetData: () => Promise<void>; // resets all data and closes the purchases instance
}

export interface UserState {
  premium: boolean;
  essential: boolean;
  customerInfoApi: CustomerInfoApi | CustomerInfo | null;
  userSubscription: any;
}

export interface Recording {
  useTemplate: boolean;
  templateId: string;
  name: string;
  duration: unknown;
  date: number;
  file: string;
  storagePath: string;
  blob: Blob;
}

const UserContext = createContext<UserProps | null>(null);

// Export context for easy usage
export const useUserProvider = () => {
  return useContext(UserContext) as UserProps;
};

let purchases: Purchases | null = null;

export const UserProvider = ({ children }: any) => {
  const [revcatUser, setRevcatUser] = useState<UserState>({
    customerInfoApi: null,
    premium: false,
    essential: false,
    userSubscription: null,
  });
  const [isReady, setIsReady] = useState(false);

  const [amplifyUser, setAmplifyUser] = useState<unknown>(null);

  const [recordings, setRecordings] = useState<Recording[]>([]);

  const [mixpanel, setMixpanel] = useState({});

  const initializedRef = useRef(false);

  // Template migration trigger hook
  const { triggerMigration } = useTemplateMigrationTrigger({
    enabled: true,
    autoStart: true,
    onSuccess: () => {
      console.log('[USER-PROVIDER] Template migration completed successfully');
    },
    onError: (error: string) => {
      console.warn('[USER-PROVIDER] Template migration failed:', error);
      // Migration failure should not prevent user login, just log the error
    },
    onProgress: (progress) => {
      console.log('[USER-PROVIDER] Template migration progress:', progress);
    }
  });

  // Recording transcription sync is now handled in specific recording components

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    initAmplify().then(async () => {
      initRevcat();
      initAnalytics();
      
      // Wait for user authentication before triggering migrations
      // Add a delay to ensure Supabase auth is ready
      setTimeout(async () => {
        try {
          console.log('[USER-PROVIDER] Checking authentication state for migration...');
          
          // Check if user is authenticated with Supabase or Amplify
          let userId: string | undefined;
          let userEmail: string | undefined;
          
          try {
            // Try Supabase first
            const { data: supabaseUser } = await supabase.auth.getUser();
            if (supabaseUser?.user?.id && supabaseUser?.user?.email) {
              userId = supabaseUser.user.id;
              userEmail = supabaseUser.user.email;
              console.log('[USER-PROVIDER] Found Supabase authenticated user');
            } else {
              throw new Error('No Supabase session');
            }
          } catch (supabaseError) {
            // Fallback to Amplify auth
            try {
              const amplifyUser = await getCurrentUser();
              const amplifyAttributes = await fetchUserAttributes();
              
              userId = amplifyUser.userId || amplifyUser.username;
              userEmail = amplifyAttributes.email;
              
              console.log('[USER-PROVIDER] Found Amplify authenticated user for migration');
            } catch (amplifyError) {
              console.log('[USER-PROVIDER] No authentication found in either system');
            }
          }
          
          if (userId && userEmail) {
            console.log('[USER-PROVIDER] User authenticated, starting migrations...');
            
            // First, ensure user is migrated to Supabase
            try {
              await userMigrationService.checkAndMigrateUserOnLogin();
              console.log('[USER-PROVIDER] User migration check completed');
            } catch (error) {
              console.warn('[USER-PROVIDER] User migration failed:', error);
              // User migration failure should not prevent template migration
            }
            
            // Trigger template migration after user migration
            triggerMigration();
          } else {
            console.warn('[USER-PROVIDER] User not authenticated yet, skipping migration');
          }
        } catch (error) {
          console.error('[USER-PROVIDER] Authentication check failed:', error);
        }
      }, 2000); // Wait 2 seconds for authentication to be ready
    });
  }, [triggerMigration]);

  // Initialize RevenueCat
  const initRevcat = async () => {
    try {
      console.log("Revcat - initializing API");
      const user: GetCurrentUserOutput = await getCurrentUser();
      const appUserId = user.username;
      if (!purchases) {
        Purchases.setLogLevel(LogLevel.Verbose);
        const initPurchases = Purchases.configure(
          process.env.EXPO_PUBLIC_REVCAT_WEB || "",
          appUserId,
        );
        purchases = initPurchases;
      }

      const customerInfo = await purchases?.getCustomerInfo();
      const endpoint = "https://api.revenuecat.com/v1/subscribers/";
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bear ${process.env.EXPO_PUBLIC_REVCAT_WEB}`,
      };
      const userSubscription: any = (
        await axios.get(endpoint + appUserId, { headers: headers })
      ).data;
      const entitlements = userSubscription?.subscriber?.entitlements || {};
      const productIdentifier = entitlements?.Premium?.product_identifier;
      if (Object.keys(entitlements).length === 0) {
        console.log("No entitlements found, reinitializing");
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await initRevcat();
        return;
      }
      if (productIdentifier) {
        const currentSubscription =
          userSubscription?.subscriber?.subscriptions[productIdentifier];
        userSubscription["current_subscription"] = currentSubscription;
      }
      if (Object.keys(entitlements).length > 0) {
        const newUser = await updateCustomerInfoApi(
          customerInfo,
          userSubscription,
        );
        setRevcatUser(newUser);
        if (Object.keys(entitlements).length > 0) {
          setIsReady(true);
        }
        return newUser;
      }
      return null;
    } catch (err) {
      console.error("Error configuring RevenueCat: ", err);
      return null;
    }
  };

  const revokeRevcatPromotion = async () => {
    const user: GetCurrentUserOutput = await getCurrentUser();
    const appUserId = user.username;
    try {
      const customerInfo = await purchases?.getCustomerInfo();
      const activeEntitlementIdentifier =
        customerInfo?.entitlements.active["Premium"].identifier ||
        customerInfo?.entitlements.active["Essential"].identifier;
      if (!activeEntitlementIdentifier) {
        return;
      }
      const postRevokePromotionals = post({
        apiName: "StripeBackend",
        path: `/stripe/revenuecat/revoke-promotionals`,
        options: {
          body: {
            appUserId,
            activeEntitlementIdentifier,
          },
        },
      });
      const resp = await postRevokePromotionals.response;
      const result: any = await resp.body.json();
      console.log("result", result);
    } catch (err) {
      console.error("Error revoking RevenueCat: ", err);
    }
  };

  const getAmplifyUser = async () => {
    const user: GetCurrentUserOutput = await getCurrentUser();
    // get user stats
    const varsGetUser: GetUserQueryVariables = {
      id: user.username,
    };
    const userStatsData = await client.graphql<GraphQLQuery<GetUserQuery>>({
      query: getUser,
      variables: varsGetUser,
    });
    const userStats = userStatsData.data.getUser;
    return { userStats, user };
  };

  const initAmplify = async () => {
    const { userStats, user } = await getAmplifyUser();
    setAmplifyUser(userStats);

    const varsUpdateUser: OnUpdateUserSubscriptionVariables = {
      filter: {
        id: { eq: user.username },
      },
    };
    const updateSub = client
      .graphql<GraphQLSubscription<OnUpdateUserSubscription>>({
        query: onUpdateUser,
        variables: varsUpdateUser,
      })
      .subscribe({
        next: async ({ data }) => {
          if (data.onUpdateUser?.audioRetentionPeriod) {
            setAmplifyUser(data.onUpdateUser);
          } else {
            const { userStats } = await getAmplifyUser();
            console.log("userStats", userStats);
            setAmplifyUser(userStats);
          }
        },
        error: (error: unknown) => console.warn(error),
      });
    return () => {
      updateSub.unsubscribe();
    };
  };

  const initAnalytics = async () => {
    // get current user
    const user: GetCurrentUserOutput = await getCurrentUser();
    const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();

    // amplify analytics - aws pinpoint
    configureAutoTrack({
      enable: true,
      type: "session",
    });

    // mixpanel
    const mixpanel = Mixpanel;
    mixpanel.init(process.env.EXPO_PUBLIC_MIXPANEL_API_KEY as string, {
      autotrack: true,
      debug: true,
      track_pageview: true,
      persistence: "localStorage",
    });
    mixpanel.identify(user.username);
    mixpanel.people.set("$email", userInfo.email);
    setMixpanel(mixpanel);
  };

  const updateCustomerInfoApi = async (
    customerInfoApi: CustomerInfo,
    userSubscription: any,
  ) => {
    const newUser: UserState = {
      ...revcatUser,
      customerInfoApi: customerInfoApi,
      premium: false,
      essential: false,
      userSubscription,
    };

    if (customerInfoApi?.entitlements.all["Premium"] !== undefined) {
      if (
        customerInfoApi.entitlements.all["Premium"].expirationDate! >
        new Date(Date.now())
      ) {
        console.log("Web user subscribed to premium, updating user");
        newUser.premium = true;
      }
    }
    if (customerInfoApi?.entitlements.all["Essential"] !== undefined) {
      if (
        customerInfoApi.entitlements.all["Essential"].expirationDate! >
        new Date(Date.now())
      ) {
        console.log("Web user subscribed to essential, updating user");
        newUser.essential = true;
      }
    }
    return newUser;
  };

  const resetData = async () => {
    initializedRef.current = false;
    setRevcatUser({
      customerInfoApi: null,
      premium: false,
      essential: false,
      userSubscription: null,
    });
    setAmplifyUser(null);
    setRecordings([]);
    setMixpanel({});
    setIsReady(false);
    purchases?.close();
    purchases = null;
  };

  const value = {
    amplifyUser,
    revcatUser,
    recordings,
    setRecordings,
    mixpanel,
    initRevcat,
    resetData,
    revokeRevcatPromotion,
  };

  // Return empty fragment if provider is not ready (Purchase not yet initialized)
  if (!isReady) return <BoxLoader className={"box-loader-detail"} />;

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export interface CustomerInfoApi {
  request_date: string;
  request_date_ms: number;
  subscriber: Subscriber;
}

export interface Subscriber {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entitlements: any;
  first_seen: string;
  last_seen: string;
  management_url: unknown;
  non_subscriptions: NonSubscriptions;
  original_app_user_id: string;
  original_application_version: string;
  original_purchase_date: string;
  other_purchases: OtherPurchases;
  subscriber_attributes: SubscriberAttributes;
  subscriptions: unknown;
}

export interface Premium {
  expires_date: string;
  grace_period_expires_date: unknown;
  product_identifier: string;
  purchase_date: string;
}

export interface SubscriberAttributes {
  $attConsentStatus: AttConsentStatus;
}

export interface AttConsentStatus {
  updated_at_ms: number;
  value: string;
}

// dont need the rest of these ever probably
export interface Subscriptions {
  vetscribepremium_5999_1m: Vetscribepremium59991m;
}

export interface Vetscribepremium59991m {
  auto_resume_date: any;
  billing_issues_detected_at: any;
  expires_date: string;
  grace_period_expires_date: any;
  is_sandbox: boolean;
  original_purchase_date: string;
  ownership_type: string;
  period_type: string;
  purchase_date: string;
  refunded_at: any;
  store: string;
  store_transaction_id: string;
  unsubscribe_detected_at: string;
}

export interface Entitlements {
  Premium: Premium;
}

export interface NonSubscriptions {}

export interface OtherPurchases {}
